
import React, { useState, useEffect } from 'react';
import { useCollegeSearch, College, CollegeFilters } from '@/hooks/useCollegeSearch';
import CollegeSearchFilters from './CollegeSearchFilters';
import CollegeSearchDisplay from './CollegeSearchDisplay';
import CollegeModal from './CollegeModal';
import SuggestCorrectionModal from './SuggestCorrectionModal';
import { Button } from '@/components/ui/button';
import { ChevronDown, Filter, X, Search } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

const CollegeSearchTab = () => {
  // Use the hook with updated implementation including pagination
  const {
    colleges,
    loading,
    error,
    toggleSaveCollege,
    searchColleges,
    loadMore,
    loadingMore,
    hasMore,
    totalCount,
    filters,
    setFilters,
    loadColleges,
    filterOptions
  } = useCollegeSearch();
  
  console.log("🎯 Final colleges being passed to UI:", colleges);
  
  const [selectedCollege, setSelectedCollege] = useState<College | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Add state for college correction modal
  const [suggestedCollege, setSuggestedCollege] = useState<College | null>(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  
  // New state for search input
  const [searchInput, setSearchInput] = useState('');

  // Load initial colleges on component mount
  useEffect(() => {
    // Adding an initial search to load some colleges on mount
    searchColleges("");
  }, []);
  
  const handleCollegeClick = (college: College) => {
    setSelectedCollege(college);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCollege(null);
  };

  const handleSuggestCorrection = (collegeId: string) => {
    const college = colleges.find(c => c.id === collegeId);
    if (college) {
      setSuggestedCollege(college);
      setShowCorrectionModal(true);
    }
  };
  
  const handleCloseCorrectionModal = () => {
    setShowCorrectionModal(false);
    setSuggestedCollege(null);
  };
  
  const handleApplyFilters = () => {
    loadColleges(); // Reload colleges when filters are applied
    toast("Filters applied successfully");
  };
  
  // New search handling with debounced search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    setFilters(prev => ({ ...prev, searchQuery: value }));
    
    // Debounce the search - wait 300ms after user stops typing
    setTimeout(() => {
      loadColleges();
    }, 300);
  };
  
  const handleClearSearch = () => {
    setSearchInput('');
    setFilters(prev => ({ ...prev, searchQuery: '' }));
    loadColleges();
  };
  
  const handleLoadMore = () => {
    if (hasMore) {
      loadMore();
    }
  };
  
  // Filter pill handlers
  const handleDivisionChange = (division: string) => {
    console.log('🎯 Division filter changing to:', division);
    console.log('🎯 Current filters before change:', filters);
    setFilters(prev => {
      const newFilters = { ...prev, divisions: division ? [division] : [] };
      console.log('🎯 New filters after change:', newFilters);
      return newFilters;
    });
  };

  const handleStateChange = (state: string) => {
    console.log('🏞️ State filter changing to:', state);
    console.log('🏞️ Current filters before change:', filters);
    setFilters(prev => {
      const newFilters = { ...prev, states: state ? [state] : [] };
      console.log('🏞️ New filters after change:', newFilters);
      return newFilters;
    });
  };

  // Add useEffect to trigger loadColleges when filters change
  useEffect(() => {
    const hasActiveFilters = 
      filters.divisions.length > 0 || 
      filters.states.length > 0 || 
      filters.conferences.length > 0 ||
      filters.religiousAffiliations.length > 0 ||
      filters.sports.length > 0 ||
      filters.tuitionMin !== null || 
      filters.tuitionMax !== null ||
      filters.satMin !== null ||
      filters.satMax !== null ||
      filters.actMin !== null ||
      filters.actMax !== null ||
      filters.gpaMin !== null ||
      filters.gpaMax !== null;
    
    if (hasActiveFilters) {
      console.log('🔄 useEffect triggered loadColleges with filters:', filters);
      loadColleges();
    }
  }, [
    filters.divisions, 
    filters.states, 
    filters.conferences,
    filters.religiousAffiliations,
    filters.sports,
    filters.tuitionMin, 
    filters.tuitionMax,
    filters.satMin,
    filters.satMax,
    filters.actMin,
    filters.actMax,
    filters.gpaMin,
    filters.gpaMax
  ]);
  
  const handleSportChange = (sport: string) => {
    setFilters(prev => ({ ...prev, sports: sport ? [sport] : [] }));
  };
  
  const getActiveSport = () => {
    return filters.sports.length > 0 ? filters.sports[0] : 'All Sports';
  };
  
  const handleTuitionChange = (min: number | null, max: number | null) => {
    setFilters(prev => ({ ...prev, tuitionMin: min, tuitionMax: max }));
    // Don't call loadColleges() here since useEffect will handle it
  };
  
  // Tuition ranges for the dropdown
  const tuitionRanges = [
    { label: 'Under $10,000', min: null, max: 10000 },
    { label: '$10,000 - $20,000', min: 10000, max: 20000 },
    { label: '$20,000 - $30,000', min: 20000, max: 30000 },
    { label: '$30,000 - $50,000', min: 30000, max: 50000 },
    { label: 'Over $50,000', min: 50000, max: null },
    { label: 'Any', min: null, max: null }
  ];
  
  const getActiveDivision = () => {
    return filters.divisions.length > 0 ? filters.divisions[0] : 'All Divisions';
  };
  
  const getActiveState = () => {
    return filters.states.length > 0 ? filters.states[0] : 'All States';
  };
  
  const getActiveTuition = () => {
    if (filters.tuitionMin === null && filters.tuitionMax === null) return 'Any Tuition';
    if (filters.tuitionMin === null && filters.tuitionMax) return `Under $${filters.tuitionMax.toLocaleString()}`;
    if (filters.tuitionMin && filters.tuitionMax === null) return `Over $${filters.tuitionMin.toLocaleString()}`;
    return `$${filters.tuitionMin?.toLocaleString()} - $${filters.tuitionMax?.toLocaleString()}`;
  };
  
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-medium mb-4">College Search</h2>
      
      {/* Unified Search Bar like Athlete Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-stretch">
          {/* Search Input */}
          <div className="relative flex-1 min-w-0">
            <Input
              type="text"
              placeholder="Search colleges..."
              value={searchInput}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-3 h-12 rounded-lg border-gray-200 w-full text-base"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search className="h-5 w-5" />
            </div>
            {searchInput && (
              <button 
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Sport Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="h-12 px-4 min-w-[130px] justify-between bg-white border-gray-200 hover:bg-gray-50"
              >
                <span className="truncate">{getActiveSport()}</span>
                <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2 bg-white z-50">
              <div className="flex flex-col gap-1 max-h-80 overflow-y-auto">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleSportChange('')}
                  className={filters.sports.length === 0 ? "bg-primary/10 text-primary" : ""}
                >
                  All Sports
                </Button>
                {filterOptions.sports.map((sport) => (
                  <Button 
                    key={sport.label} 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleSportChange(sport.label)}
                    className={filters.sports[0] === sport.label ? "bg-primary/10 text-primary" : ""}
                  >
                    {sport.label}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Division Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="h-12 px-4 min-w-[140px] justify-between bg-white border-gray-200 hover:bg-gray-50"
              >
                <span className="truncate">{getActiveDivision()}</span>
                <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2 bg-white z-50">
              <div className="flex flex-col gap-1 max-h-80 overflow-y-auto">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleDivisionChange('')}
                  className={filters.divisions.length === 0 ? "bg-primary/10 text-primary" : ""}
                >
                  All Divisions
                </Button>
                {filterOptions.divisions.map((division) => (
                  <Button 
                    key={division} 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDivisionChange(division)}
                    className={filters.divisions[0] === division ? "bg-primary/10 text-primary" : ""}
                  >
                    {division}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* State Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="h-12 px-4 min-w-[140px] justify-between bg-white border-gray-200 hover:bg-gray-50"
              >
                <span className="truncate">{getActiveState()}</span>
                <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2 bg-white z-50">
              <div className="flex flex-col gap-1 max-h-80 overflow-y-auto">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleStateChange('')}
                  className={filters.states.length === 0 ? "bg-primary/10 text-primary" : ""}
                >
                  All States
                </Button>
                {filterOptions.states.map((state) => (
                  <Button 
                    key={state} 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleStateChange(state)}
                    className={filters.states[0] === state ? "bg-primary/10 text-primary" : ""}
                  >
                    {state}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Tuition Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="h-12 px-4 min-w-[140px] justify-between bg-white border-gray-200 hover:bg-gray-50"
              >
                <span className="truncate">{getActiveTuition()}</span>
                <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2 bg-white z-50">
              <div className="flex flex-col gap-1">
                {tuitionRanges.map((range, index) => (
                  <Button 
                    key={index} 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleTuitionChange(range.min, range.max)}
                    className={
                      filters.tuitionMin === range.min && filters.tuitionMax === range.max 
                        ? "bg-primary/10 text-primary" 
                        : ""
                    }
                  >
                    {range.label}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Clear/More Filters Button */}
          <Button 
            variant="outline"
            className="h-12 px-4 min-w-[120px] bg-white border-gray-200 hover:bg-gray-50"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'More Filters'}
          </Button>
        </div>
      </div>
      
      {/* Active Filters Badges */}
      <div className="flex flex-wrap gap-2">
        {filters.divisions.length > 0 && (
          <Badge variant="secondary" className="flex items-center gap-1">
            Division: {filters.divisions[0]}
            <X 
              className="h-3 w-3 cursor-pointer" 
              onClick={() => {
                setFilters(prev => ({ ...prev, divisions: [] }));
                loadColleges();
              }} 
            />
          </Badge>
        )}
        {filters.states.length > 0 && (
          <Badge variant="secondary" className="flex items-center gap-1">
            State: {filters.states[0]}
            <X 
              className="h-3 w-3 cursor-pointer" 
              onClick={() => {
                setFilters(prev => ({ ...prev, states: [] }));
                loadColleges();
              }} 
            />
          </Badge>
        )}
        {(filters.tuitionMin !== null || filters.tuitionMax !== null) && (
          <Badge variant="secondary" className="flex items-center gap-1">
            Tuition: {getActiveTuition()}
            <X 
              className="h-3 w-3 cursor-pointer" 
              onClick={() => {
                setFilters(prev => ({ ...prev, tuitionMin: null, tuitionMax: null }));
                loadColleges();
              }} 
            />
          </Badge>
        )}
        {searchInput && (
          <Badge variant="secondary" className="flex items-center gap-1">
            Search: {searchInput}
            <X 
              className="h-3 w-3 cursor-pointer" 
              onClick={handleClearSearch} 
            />
          </Badge>
        )}
      </div>
      
      {/* Advanced Filters Collapsible */}
      <Collapsible open={showFilters} onOpenChange={setShowFilters} className="w-full">
        <CollapsibleContent className="mb-6">
          <CollegeSearchFilters
            filters={filters}
            setFilters={setFilters}
            onApplyFilters={handleApplyFilters}
            filterOptions={filterOptions}
            loading={loading}
          />
        </CollapsibleContent>
      </Collapsible>
      
      <div className="mb-3">
        {totalCount > 0 && (
          <div className="text-sm text-gray-500">
            Showing {colleges.length} of {totalCount} colleges
          </div>
        )}
      </div>
      
      <CollegeSearchDisplay
        colleges={colleges}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onToggleSave={toggleSaveCollege}
        showSavedDetails={false}
        onCollegeClick={handleCollegeClick}
        onSuggestCorrection={handleSuggestCorrection}
      />
      
      {selectedCollege && (
        <CollegeModal
          college={selectedCollege}
          open={modalOpen}
          onClose={handleCloseModal}
          onToggleSave={toggleSaveCollege}
          onSuggestCorrection={handleSuggestCorrection}
        />
      )}
      
      {/* Add the suggestion correction modal */}
      <SuggestCorrectionModal
        college={suggestedCollege}
        open={showCorrectionModal}
        onClose={handleCloseCorrectionModal}
      />
    </div>
  );
};

export default CollegeSearchTab;
